/**
 * Carrousel en boucle
 *
 * Le carrousel de Prestige s'arrête aux deux bouts : arrivé à la dernière
 * image, la flèche « suivant » se désactive et le geste ne donne plus rien.
 * Sur une galerie produit, on attend l'inverse : on continue vers la droite
 * et ça revient à la première.
 *
 * S'applique aux seuls carrousels portant l'attribut « loop ».
 * Quatre chemins mènent à l'image suivante, il faut les couvrir tous :
 *
 * 1. next() / previous(), qui bornent au premier et au dernier élément ;
 * 2. les flèches, que le thème désactive à l'approche d'un bord ;
 * 3. le balayage tactile, que le défilement natif ne prolonge pas au-delà
 *    du bord ;
 * 4. la molette / le trackpad, même problème sur ordinateur.
 *
 * Les gestes valent aussi sur la bande de miniatures : arrivé à la dernière
 * vignette, continuer vers la droite ramène à la première.
 */
(() => {
  // Un vrai balayage, pas une hésitation du doigt.
  const SEUIL_GESTE = 60;

  // La molette envoie beaucoup de petits deltas : on les cumule.
  const SEUIL_MOLETTE = 100;

  // Sans événement pendant ce délai, le geste suivant repart de zéro.
  const REPOS_MOLETTE = 300;

  /**
   * Le passage d'un bout à l'autre est instantané, jamais en douceur : un
   * défilement animé rembobinerait toute la bande vers la gauche sous les
   * yeux du client. Le saut sec se lit comme « une image de plus ».
   */
  const SAUT = { instant: true };

  const enBoucle = (carrousel) => carrousel.hasAttribute('loop');

  const defilable = (zone) => zone.scrollWidth - zone.clientWidth > 2;
  const auBout = (zone) => zone.scrollLeft >= zone.scrollWidth - zone.clientWidth - 2;
  const auDebut = (zone) => zone.scrollLeft <= 2;

  /** Bande de miniatures et pastilles rattachées à un carrousel. */
  const bandes = (carrousel) => {
    if (!carrousel.id) return [];

    const cible = `[aria-controls="${CSS.escape(carrousel.id)}"]`;

    return document.querySelectorAll(`product-gallery-navigation${cible}, carousel-navigation${cible}`);
  };

  /**
   * Saut d'un bout à l'autre.
   * La bande de miniatures suit le carrousel en défilement doux : sans ce
   * recalage, l'image sauterait mais les vignettes, elles, rembobineraient
   * une à une. On les recale d'un coup, juste après.
   */
  const sauterVers = (carrousel, index) => {
    const resultat = carrousel.select(index, SAUT);
    const versDebut = index === 0;

    bandes(carrousel).forEach((bande) => {
      bande.scrollTo({
        left: versDebut ? 0 : bande.scrollWidth,
        top: versDebut ? 0 : bande.scrollHeight,
        behavior: 'auto',
      });
    });

    return resultat;
  };

  /**
   * Déplacement d'un cran, avec passage d'un bout à l'autre.
   * Reprend le calcul du thème (bornes + « group-cells ») et n'ajoute que
   * le bouclage quand on est déjà sur la dernière ou la première image.
   */
  const aller = (carrousel, sens, options = {}) => {
    const dernier = carrousel.cells.length - 1;
    const actuel = carrousel.selectedIndex;

    if (sens > 0 && actuel >= dernier) return sauterVers(carrousel, 0);
    if (sens < 0 && actuel <= 0) return sauterVers(carrousel, dernier);

    const cible = Math.min(Math.max(actuel + sens * carrousel.groupCells, 0), dernier);

    return carrousel.select(cible, options);
  };

  /**
   * 1. Bouclage de la navigation
   * Les méthodes sont reprises sur le prototype : elles couvrent d'un coup
   * les flèches, les miniatures et le clavier, sans dépendre du moment où
   * chaque carrousel apparaît dans la page.
   */
  customElements.whenDefined('scroll-carousel').then(() => {
    const prototype = customElements.get('scroll-carousel').prototype;
    const suivantOriginal = prototype.next;
    const precedentOriginal = prototype.previous;

    prototype.next = function (options = {}) {
      return enBoucle(this) ? aller(this, 1, options) : suivantOriginal.call(this, options);
    };

    prototype.previous = function (options = {}) {
      return enBoucle(this) ? aller(this, -1, options) : precedentOriginal.call(this, options);
    };
  });

  /**
   * 2. Flèches toujours actives
   * Le thème désactive le bouton en écoutant « scroll:edge-nearing » sur le
   * carrousel. L'événement ne remonte pas, mais il passe quand même par la
   * phase de capture : on l'arrête au niveau du document, avant qu'il
   * n'atteigne les boutons. Déterministe, contrairement à un écouteur posé
   * sur le carrousel — le thème réinstalle les siens à chaque
   * reconstruction de la galerie et l'ordre ne serait pas garanti.
   */
  document.addEventListener(
    'scroll:edge-nearing',
    (event) => {
      const carrousel = event.target;

      if (carrousel instanceof Element && enBoucle(carrousel)) {
        event.stopPropagation();
      }
    },
    true
  );

  /** Passe d'un bout à l'autre, si la zone est bien arrivée au bord. */
  const boucler = (zone, carrousel, sens) => {
    if (!defilable(zone)) return;

    if (sens > 0 && auBout(zone)) {
      sauterVers(carrousel, 0);
    } else if (sens < 0 && auDebut(zone)) {
      sauterVers(carrousel, carrousel.cells.length - 1);
    }
  };

  /**
   * 3 et 4. Gestes au-delà du bord
   * Collée au bord, la zone ne défile plus : le geste ne produit aucun
   * scroll. On le lit donc à la main, et un mouvement franc dans le vide
   * fait passer de l'autre côté.
   */
  const brancherGestes = (zone, carrousel) => {
    // --- Tactile ---
    let depart = null;

    zone.addEventListener(
      'touchstart',
      (event) => {
        depart =
          event.touches.length === 1
            ? { x: event.touches[0].clientX, y: event.touches[0].clientY, scrollLeft: zone.scrollLeft }
            : null;
      },
      { passive: true }
    );

    zone.addEventListener(
      'touchend',
      (event) => {
        if (!depart) return;

        const { x, y, scrollLeft } = depart;
        const dx = event.changedTouches[0].clientX - x;
        const dy = event.changedTouches[0].clientY - y;

        depart = null;

        // Geste trop court, ou vertical : c'est la page qu'on fait défiler.
        if (Math.abs(dx) < SEUIL_GESTE || Math.abs(dy) > Math.abs(dx)) return;

        // La zone a bougé : le défilement natif a fait le travail.
        if (Math.abs(zone.scrollLeft - scrollLeft) > 2) return;

        boucler(zone, carrousel, dx < 0 ? 1 : -1);
      },
      { passive: true }
    );

    // --- Molette et trackpad ---
    let cumul = 0;
    let dernierEvenement = 0;
    let verrou = false;

    // Écouteur NON passif, volontairement : après le saut il faut pouvoir
    // absorber la fin du geste avec preventDefault(). Sinon l'inertie du
    // trackpad, qui continue d'envoyer des deltas pendant une demi-seconde,
    // fait défiler la première image vers la deuxième — on croit que le
    // carrousel « saute » l'image du début tout seul.
    zone.addEventListener(
      'wheel',
      (event) => {
        // Défilement vertical de la page : ça ne nous concerne pas.
        if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;

        // Une pause franche = nouveau geste, on repart à zéro.
        if (event.timeStamp - dernierEvenement > REPOS_MOLETTE) {
          cumul = 0;
          verrou = false;
        }

        dernierEvenement = event.timeStamp;

        // Queue d'inertie du saut précédent : on la mange.
        if (verrou) {
          event.preventDefault();
          return;
        }

        const sens = event.deltaX > 0 ? 1 : -1;

        // On ne cumule que ce qui pousse dans le vide, une fois collé au bord.
        if ((sens > 0 && !auBout(zone)) || (sens < 0 && !auDebut(zone))) {
          cumul = 0;
          return;
        }

        cumul += Math.abs(event.deltaX);

        if (cumul < SEUIL_MOLETTE) return;

        cumul = 0;
        verrou = true;
        event.preventDefault();

        boucler(zone, carrousel, sens);
      },
      { passive: false }
    );
  };

  const equipees = new WeakSet();

  const recenser = () => {
    document.querySelectorAll('scroll-carousel[loop]').forEach((carrousel) => {
      // Les flèches ont pu être désactivées avant le chargement du script.
      if (carrousel.id) {
        const cible = `[aria-controls="${CSS.escape(carrousel.id)}"]`;

        document.querySelectorAll(`carousel-prev-button${cible}, carousel-next-button${cible}`).forEach((commande) => {
          if (commande.firstElementChild instanceof HTMLButtonElement) {
            commande.firstElementChild.disabled = false;
          }
        });
      }

      if (!equipees.has(carrousel)) {
        equipees.add(carrousel);
        brancherGestes(carrousel, carrousel);
      }

      // Même bouclage sur la bande de miniatures : arrivé à la dernière
      // vignette, continuer vers la droite ramène à la première image.
      bandes(carrousel).forEach((bande) => {
        if (equipees.has(bande)) return;

        equipees.add(bande);
        brancherGestes(bande, carrousel);
      });
    });
  };

  recenser();
  document.addEventListener('DOMContentLoaded', recenser);
  document.addEventListener('shopify:section:load', recenser);

  // La galerie est reconstruite à chaque changement de variante.
  let recensementEnAttente;
  new MutationObserver(() => {
    clearTimeout(recensementEnAttente);
    recensementEnAttente = setTimeout(recenser, 100);
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
